import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Save, RotateCcw, Plus, Trash2, Share2, Sparkles, ArrowUpDown } from "lucide-react";
import { LINES, getLine } from "@/lib/lines";
import StationAutocomplete from "./StationAutocomplete";
import StationsPicker from "./StationsPicker";

const TRAIN_TYPES = [
  "All Stations",
  "Limited Stops",
  "Express",
  "Intercity",
  "Non-Stop",
];

const inputCls =
  "rounded-none bg-[#111] border-[#2A2A2A] text-white placeholder:text-white/30 " +
  "focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 " +
  "focus-visible:ring-offset-[#0A0A0A] font-mono";

export default function ControlPanel({
  form, setForm, onSave, onReset, saving, editingId, mode, onShare,
}) {
  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const isRailcar = mode === "railcar";
  const isPlatform = mode === "platform";

  // Railcar pages helpers
  const pages = Array.isArray(form.railcar_pages) ? form.railcar_pages : [];
  const updatePage = (idx, key, value) => {
    setForm((f) => {
      const next = [...(f.railcar_pages || [])];
      next[idx] = { ...next[idx], [key]: value };
      return { ...f, railcar_pages: next };
    });
  };
  const addPage = () => {
    setForm((f) => ({
      ...f,
      railcar_pages: [...(f.railcar_pages || []), { top: "", bottom: "" }],
    }));
  };
  const removePage = (idx) => {
    setForm((f) => ({
      ...f,
      railcar_pages: (f.railcar_pages || []).filter((_, i) => i !== idx),
    }));
  };

  const SectionLabel = ({ children, htmlFor, badge }) => (
    <div className="flex items-center justify-between">
      <Label
        htmlFor={htmlFor}
        className="font-mono uppercase tracking-[0.25em] text-[10px] text-white/60"
      >
        {children}
      </Label>
      {badge && (
        <span className="font-mono text-[10px] tracking-widest text-white/30">
          {badge}
        </span>
      )}
    </div>
  );

  return (
    <div
      className="bg-[#111] border border-[#2A2A2A] p-5 space-y-5"
      data-testid="control-panel"
    >
      <div className="flex items-baseline justify-between border-b border-[#2A2A2A] pb-3">
        <h2 className="font-display font-bold uppercase tracking-tight text-xl text-white">
          Operator Console
        </h2>
        <span className="font-mono text-[10px] tracking-[0.3em] text-white/40">
          {editingId ? "EDITING" : "NEW BOARD"}
        </span>
      </div>

      {/* Preset name */}
      <div className="space-y-1.5">
        <SectionLabel htmlFor="preset-name">Preset name</SectionLabel>
        <Input
          id="preset-name"
          data-testid="preset-name-input"
          value={form.name}
          onChange={update("name")}
          placeholder="e.g. T1 Central → Penrith"
          className={inputCls}
        />
      </div>

      {/* Line + Train type */}
      {!isRailcar && (
      <>
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
        <div className="space-y-1.5 sm:col-span-2">
          <SectionLabel htmlFor="line-code">Line</SectionLabel>
          <Select
            value={form.line_code}
            onValueChange={(v) => {
              const line = getLine(v);
              setForm((f) => ({
                ...f,
                line_code: v,
                // Automatically flip on booking + fare/duration for premium services;
                // don't flip OFF for suburban lines — leave user's choice alone.
                booking_required: line?.bookingRequired ? true : f.booking_required,
                show_fare_duration: line?.bookingRequired ? true : f.show_fare_duration,
              }));
            }}
          >
            <SelectTrigger
              id="line-code"
              data-testid="line-code-select"
              className={`${inputCls} rounded-none`}
            >
              <SelectValue placeholder="T1" />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-[#111] border-[#2A2A2A] text-white max-h-[320px]">
              {LINES.map((l) => (
                <SelectItem key={l.code} value={l.code} className="font-mono">
                  <span className="flex items-center gap-2.5">
                    <span
                      className="inline-flex items-center justify-center font-bold text-[10px] w-7 h-5 rounded-sm"
                      style={{ backgroundColor: l.color, color: l.textColor }}
                    >
                      {l.code}
                    </span>
                    <span className="text-[12px] text-white/80">{l.label}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 sm:col-span-3">
          <SectionLabel htmlFor="train-type">Train type</SectionLabel>
          <Select value={form.train_type} onValueChange={update("train_type")}>
            <SelectTrigger
              id="train-type"
              data-testid="train-type-select"
              className={`${inputCls} rounded-none`}
            >
              <SelectValue placeholder="All Stations" />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-[#111] border-[#2A2A2A] text-white">
              {TRAIN_TYPES.map((t) => (
                <SelectItem key={t} value={t} className="font-mono">
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Booking required toggle (for XPT / Xplorer / Endeavour services) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between border border-[#2A2A2A] px-3 py-2.5 bg-[#0E0E0E]">
          <div className="min-w-0 pr-3">
            <Label
              htmlFor="booking-required"
              className="font-mono uppercase tracking-[0.2em] text-[10px] text-white/70 cursor-pointer"
            >
              Booking required
            </Label>
            <p className="font-mono text-[10px] text-white/40 mt-0.5">
              NSW TrainLink XPT / Xplorer / Endeavour long-distance services
            </p>
          </div>
          <Switch
            id="booking-required"
            data-testid="booking-required-switch"
            checked={!!form.booking_required}
            onCheckedChange={(v) =>
              setForm((f) => ({
                ...f,
                booking_required: v,
                // Convenience: turning booking on also switches on fare/duration
                show_fare_duration: v ? true : f.show_fare_duration,
              }))
            }
          />
        </div>

        {/* Fare + duration toggle (independent, defaults to on when booking on) */}
        <div className="flex items-center justify-between border border-[#2A2A2A] px-3 py-2.5 bg-[#0E0E0E]">
          <div className="min-w-0 pr-3">
            <Label
              htmlFor="show-fare-duration"
              className="font-mono uppercase tracking-[0.2em] text-[10px] text-white/70 cursor-pointer"
            >
              Show fare &amp; duration
            </Label>
            <p className="font-mono text-[10px] text-white/40 mt-0.5">
              Display trip cost and travel time on the board
            </p>
          </div>
          <Switch
            id="show-fare-duration"
            data-testid="show-fare-duration-switch"
            checked={!!form.show_fare_duration}
            onCheckedChange={(v) =>
              setForm((f) => ({ ...f, show_fare_duration: v }))
            }
          />
        </div>

        {/* Fare + duration inputs (only visible when the toggle is on) */}
        {form.show_fare_duration && (
          <div className="grid grid-cols-2 gap-3" data-testid="fare-duration-fields">
            <div className="space-y-1.5">
              <SectionLabel htmlFor="fare">Fare</SectionLabel>
              <Input
                id="fare"
                data-testid="fare-input"
                value={form.fare}
                onChange={update("fare")}
                placeholder="$32.85 economy"
                className={inputCls}
              />
            </div>
            <div className="space-y-1.5">
              <SectionLabel htmlFor="duration">Duration</SectionLabel>
              <Input
                id="duration"
                data-testid="duration-input"
                value={form.duration}
                onChange={update("duration")}
                placeholder="11h 05m"
                className={inputCls}
              />
            </div>
          </div>
        )}
      </div>

      {/* Destination */}
      <div className="space-y-1.5">
        <SectionLabel htmlFor="destination" badge="HERO">Destination</SectionLabel>
        <StationAutocomplete
          value={form.destination}
          onChange={(v) => setForm((f) => ({ ...f, destination: v }))}
          placeholder="e.g. Penrith via Strathfield"
          inputClassName={inputCls + " border bg-[#111] border-[#2A2A2A] text-white rounded-none"}
          testId="destination-input"
        />
      </div>

      {/* Time + Platform */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <SectionLabel htmlFor="departure-time">Departs</SectionLabel>
          <Input
            id="departure-time"
            data-testid="departure-time-input"
            value={form.departure_time}
            onChange={update("departure_time")}
            placeholder="HH:MM"
            className={inputCls}
          />
        </div>
        <div className="space-y-1.5">
          <SectionLabel htmlFor="platform">Platform</SectionLabel>
          <Input
            id="platform"
            data-testid="platform-input"
            value={form.platform}
            onChange={update("platform")}
            placeholder="e.g. 6"
            className={inputCls}
          />
        </div>
      </div>

      {/* Stops list */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <SectionLabel htmlFor="stops">Stops</SectionLabel>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                const stops = form.stops || [];
                if (stops.length < 2) return;
                const reversed = [...stops].reverse();
                setForm((f) => {
                  const patch = { stops: reversed };
                  // If destination matches the previous last stop, follow the reversal
                  const prevLast = stops[stops.length - 1];
                  if (f.destination && f.destination.trim() === prevLast) {
                    patch.destination = reversed[reversed.length - 1];
                  }
                  return { ...f, ...patch };
                });
              }}
              data-testid="reverse-stops-button"
              disabled={(form.stops || []).length < 2}
              className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/70 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Reverse the order of stops"
            >
              <ArrowUpDown className="w-3 h-3" />
              Reverse
            </button>
            <button
              type="button"
              onClick={() => {
                const line = getLine(form.line_code);
                const linestops = line?.stops || [];
                if (linestops.length === 0) return;
                setForm((f) => {
                  const patch = { stops: linestops };
                  if (!f.destination || !f.destination.trim()) {
                    patch.destination = linestops[linestops.length - 1];
                  }
                  return { ...f, ...patch };
                });
              }}
              data-testid="autofill-stops-button"
              className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#FFB000] hover:text-white transition-colors"
              title={`Autofill with ${getLine(form.line_code)?.label || "line"} stops`}
            >
              <Sparkles className="w-3 h-3" />
              Autofill from {form.line_code}
            </button>
          </div>
        </div>
        <StationsPicker
          value={form.stops || []}
          onChange={(next) => setForm((f) => ({ ...f, stops: next }))}
          testIdPrefix="stops"
        />
      </div>

      {/* Announcement */}
      <div className="space-y-1.5">
        <SectionLabel htmlFor="announcement" badge="SCROLLS">
          Announcement
        </SectionLabel>
        <Textarea
          id="announcement"
          data-testid="announcement-textarea"
          value={form.announcement}
          onChange={update("announcement")}
          placeholder="e.g. Trackwork between Granville and Liverpool. Allow extra travel time."
          rows={3}
          className={inputCls}
        />
      </div>

      {/* Platform indicator extra fields */}
      {isPlatform && (
        <div className="space-y-4" data-testid="platform-fields">
          <div className="border border-[#F58220]/40 bg-[#1a1300] p-3">
            <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#F58220] mb-1">
              Platform Indicator
            </div>
            <p className="font-mono text-[11px] text-white/50 leading-relaxed">
              Only the stops list scrolls vertically. Everything else is fixed.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <SectionLabel htmlFor="departs-relative">Departs (relative)</SectionLabel>
              <Input
                id="departs-relative"
                data-testid="departs-relative-input"
                value={form.departs_relative}
                onChange={update("departs_relative")}
                placeholder="14 min"
                className={inputCls}
              />
            </div>
            <div className="space-y-1.5">
              <SectionLabel htmlFor="carriages">Carriages</SectionLabel>
              <Input
                id="carriages"
                data-testid="carriages-input"
                value={form.carriages}
                onChange={update("carriages")}
                placeholder="8 carriages"
                className={inputCls}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <SectionLabel htmlFor="stops-label">Stops label</SectionLabel>
            <Input
              id="stops-label"
              data-testid="stops-label-input"
              value={form.stops_label}
              onChange={update("stops_label")}
              placeholder="All Stops"
              className={inputCls}
            />
          </div>

          {/* Next service sub-section */}
          <div className="border border-[#2A2A2A] p-3 bg-[#0E0E0E] space-y-3">
            <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-white/60">
              Next Service
            </div>
            <div className="space-y-1.5">
              <SectionLabel htmlFor="next-destination">Destination</SectionLabel>
              <Input
                id="next-destination"
                data-testid="next-destination-input"
                value={form.next_destination}
                onChange={update("next_destination")}
                placeholder="e.g. Hurstville"
                className={inputCls}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <SectionLabel htmlFor="next-platform">Platform</SectionLabel>
                <Input
                  id="next-platform"
                  data-testid="next-platform-input"
                  value={form.next_platform}
                  onChange={update("next_platform")}
                  placeholder="e.g. 25"
                  className={inputCls}
                />
              </div>
              <div className="space-y-1.5">
                <SectionLabel htmlFor="next-departs">Departs</SectionLabel>
                <Input
                  id="next-departs"
                  data-testid="next-departs-input"
                  value={form.next_departs}
                  onChange={update("next_departs")}
                  placeholder="e.g. 19 min"
                  className={inputCls}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <SectionLabel htmlFor="next-via">Via</SectionLabel>
              <Input
                id="next-via"
                data-testid="next-via-input"
                value={form.next_via}
                onChange={update("next_via")}
                placeholder="e.g. Via Banksia"
                className={inputCls}
              />
            </div>
          </div>
        </div>
      )}
      </>
      )}

      {/* RAILCAR INTERIOR FIELDS */}
      {isRailcar && (
        <div className="space-y-4" data-testid="railcar-fields">
          <div className="border border-[#FFB000]/30 bg-[#1a1300] p-3">
            <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#FFB000] mb-1">
              Railcar Interior Indicator
            </div>
            <p className="font-mono text-[11px] text-white/50 leading-relaxed">
              M Set / H Set / Hunter dot-matrix display. Add multiple
              pages and the screen will cycle between them.
            </p>
          </div>

          {/* Pages editor */}
          <div className="space-y-3" data-testid="railcar-pages-editor">
            {pages.length === 0 && (
              <div className="font-mono text-[11px] text-white/40 italic px-1">
                No pages yet — add one below.
              </div>
            )}
            {pages.map((p, i) => (
              <div
                key={i}
                className="border border-[#2A2A2A] p-3 space-y-2 bg-[#0E0E0E]"
                data-testid={`railcar-page-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono uppercase tracking-[0.25em] text-[10px] text-white/50">
                    Page {i + 1}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removePage(i)}
                    data-testid={`railcar-page-remove-${i}`}
                    className="rounded-none h-7 w-7 text-white/50 hover:text-red-500 hover:bg-transparent"
                    aria-label="Remove page"
                    disabled={pages.length <= 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <Input
                  value={p.top || ""}
                  onChange={(e) => updatePage(i, "top", e.target.value)}
                  placeholder={i === 0 ? "Welcome aboard" : "Top row"}
                  data-testid={`railcar-page-${i}-top-input`}
                  className={inputCls}
                />
                <Input
                  value={p.bottom || ""}
                  onChange={(e) => updatePage(i, "bottom", e.target.value)}
                  placeholder={i === 0 ? "(optional bottom row)" : "Bottom row (optional)"}
                  data-testid={`railcar-page-${i}-bottom-input`}
                  className={inputCls}
                />
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={addPage}
              data-testid="railcar-add-page-button"
              className="rounded-none border-dashed border-white/30 bg-transparent text-white hover:bg-[#1a1a1a] font-mono uppercase tracking-widest text-[11px] w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add page
            </Button>
          </div>

          {/* Interval + scroll */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <SectionLabel htmlFor="railcar-interval">Cycle every</SectionLabel>
              <div className="relative">
                <Input
                  id="railcar-interval"
                  type="number"
                  min={1500}
                  step={500}
                  data-testid="railcar-interval-input"
                  value={form.railcar_interval ?? 4000}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      railcar_interval: Math.max(1500, Number(e.target.value) || 4000),
                    }))
                  }
                  className={`${inputCls} pr-10`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-[10px] text-white/40 uppercase">
                  ms
                </span>
              </div>
            </div>

            <div className="flex flex-col justify-end">
              <div className="flex items-center justify-between border border-[#2A2A2A] px-3 py-2.5 h-10">
                <Label
                  htmlFor="railcar-scroll"
                  className="font-mono uppercase tracking-[0.2em] text-[10px] text-white/70 cursor-pointer"
                >
                  Scroll bottom
                </Label>
                <Switch
                  id="railcar-scroll"
                  data-testid="railcar-scroll-switch"
                  checked={!!form.railcar_scroll}
                  onCheckedChange={(v) =>
                    setForm((f) => ({ ...f, railcar_scroll: v }))
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1">
        <Button
          onClick={onSave}
          disabled={saving}
          data-testid="save-preset-button"
          className="rounded-none bg-white text-black hover:bg-white/85 font-mono uppercase tracking-widest text-[11px] flex-1"
        >
          <Save className="w-4 h-4 mr-2" />
          {editingId ? "Update preset" : "Save preset"}
        </Button>
        <Button
          variant="outline"
          onClick={onShare}
          data-testid="share-board-button"
          className="rounded-none border-[#FFB000]/40 bg-transparent text-[#FFB000] hover:bg-[#FFB000] hover:text-black font-mono uppercase tracking-widest text-[11px]"
          aria-label="Copy share link"
        >
          <Share2 className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          onClick={onReset}
          data-testid="reset-form-button"
          className="rounded-none border-[#2A2A2A] bg-transparent text-white hover:bg-[#222] font-mono uppercase tracking-widest text-[11px]"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          New
        </Button>
      </div>
    </div>
  );
}
